import { t } from "@lingui/macro";
import {
  type ProjectTimeBillingComponentFragment,
  type ProjectTimeBillingComponentPreviewFragment,
  type ProjectTimeBillingComputedFragment,
  TimeBillingComponentKeyEnum,
} from "@src/__generated__/graphql";
import { NumberModel, TimeFieldState } from "@src/utils/models";
import { COMPONENT_KEYS_ORDER } from "./constants";
import type { TimeBillingSource, UserRowState } from "./types";

export function getOiBudgetItemDisplayName(source: TimeBillingSource): string {
  const budgetItemTitle = source.budgetItem?.title;
  const invoiceNo = source.outgoingInvoice?.invoice_no;
  if (budgetItemTitle && invoiceNo) {
    return `${invoiceNo} — ${budgetItemTitle}`;
  }
  return invoiceNo ?? source.outgoing_invoice_id ?? source.source_id ?? "";
}

export function getExpenseItemDisplayName(source: TimeBillingSource): string {
  const expenseNo =
    source.expense?.expense_no ??
    source.expense?.document_number ??
    source.expenseItem?.id ??
    source.source_id;
  const budgetItemTitle = source.expenseItem?.budgetItem?.title;
  if (expenseNo && budgetItemTitle) {
    return `${expenseNo} — ${budgetItemTitle}`;
  }
  return expenseNo ?? source.source_id ?? "";
}

export function transformComponents(
  components:
    | ProjectTimeBillingComponentFragment[]
    | ProjectTimeBillingComponentPreviewFragment[],
  denomination: number,
) {
  return components
    .map((component) => ({
      key: component.key,
      amount: NumberModel.fromDenomValue(component.amount, denomination),
      use_in_total: component.use_in_total,
    }))
    .toSorted(
      (a, b) => COMPONENT_KEYS_ORDER[a.key] - COMPONENT_KEYS_ORDER[b.key],
    );
}

export function mapPreviewRowsToUserRows(
  previewRows: ProjectTimeBillingComputedFragment["userRows"],
  denomination: number,
): UserRowState[] {
  return previewRows.map((row) => ({
    id: `${row.user_id}-${row.time_tracking_work_type_id ?? "null"}-${row.team_id ?? "null"}`,
    user_id: row.user_id ?? undefined,
    time_tracking_work_type_id: row.time_tracking_work_type_id ?? null,
    team_id: row.team_id ?? null,
    amount: NumberModel.fromDenomValue(row.amount, denomination),
    invoiced_time: new TimeFieldState(row.invoiced_time),
    overserviced_time: new TimeFieldState(row.overserviced_time),
    effective_hourly_rate_invoiced: NumberModel.fromDenomValue(
      row.effective_hourly_rate_invoiced,
      denomination,
    ),
  }));
}

export function getComponentLabel(key: TimeBillingComponentKeyEnum): string {
  switch (key) {
    case TimeBillingComponentKeyEnum.InvoicedExpenses:
      return t`Invoiced Expenses`;
    case TimeBillingComponentKeyEnum.InvoicedOurwork:
      return t`Invoiced Work`;
    case TimeBillingComponentKeyEnum.ExpensesEbi:
      return t`External Costs`;
    case TimeBillingComponentKeyEnum.ExpensesOwbi:
      return t`Work Expenses`;
    case TimeBillingComponentKeyEnum.Prepayment:
      return t`Prepayment`;
  }
}

export function getComponentDescription(
  key: TimeBillingComponentKeyEnum,
): string {
  switch (key) {
    case TimeBillingComponentKeyEnum.InvoicedExpenses:
      return t`Amount invoiced for delivered external costs.`;
    case TimeBillingComponentKeyEnum.InvoicedOurwork:
      return t`Amount invoiced for agency work and services.`;
    case TimeBillingComponentKeyEnum.ExpensesEbi:
      return t`Costs related to production, media buying, and third-party services.`;
    case TimeBillingComponentKeyEnum.ExpensesOwbi:
      return t`Costs directly related to delivering agency work.`;
    case TimeBillingComponentKeyEnum.Prepayment:
      return t`Amount covered by an existing prepayment.`;
  }
}
